/**
 * Utility functions for user-related operations.
 */

/**
 * Optimized version of usersFiltered that uses a hash map for O(1) lookup.
 * @param {Array | string} datas - An array of user objects/IDs, or a single ID.
 * @param {Object} usersMap - A map of { [id]: UserObject }.
 * @returns {Array} Formatted names.
 */
export const usersFilteredFromMap = (datas, usersMap) => {
    if (!usersMap || Object.keys(usersMap).length === 0) {
        return ['S/I']
    }

    if (datas && (Array.isArray(datas) ? datas.length > 0 : true)) {
        const process = (item) => {
            const id = (item && item._id) ? item._id : item
            const user = usersMap[id] || (item && item.name ? item : null)
            return user ? `- ${user.name} ${user.lastName}` : '- Desconocido'
        }

        if (Array.isArray(datas)) {
            return datas.map(process)
        } else {
            return [process(datas)]
        }
    }
    return ['S/I']
}

/**
 * Filters a list of users or finds a single user by ID.
 * @param {Array | string} datas - An array of user objects, or a single user ID string.
 * @param {Array<object>} allUsers - The complete list of user objects from context.
 * @returns {Array} An array of formatted user names, or a single user object if searching by ID, or a default 'Sin asignaciones' message.
 */
export const usersFiltered = (datas, allUsers) => {
    if (!allUsers || allUsers.length === 0) {
        // Fallback or error handling if users context is not ready
        return ['- Cargando usuarios...']
    }

    if (datas && datas.length > 0) {
        if (Array.isArray(datas)) {
            return datas.map((data) => {
                const idToSearch = (data && data._id) ? data._id : data
                const user = allUsers.find(u => u._id === idToSearch) || (data && data.name ? data : null)
                return user ? `- ${user.name} ${user.lastName}` : '- Usuario desconocido'
            })
        } else { // datas is a single ID or Object
            const idToSearch = (datas && datas._id) ? datas._id : datas
            const user = allUsers.find(u => u._id === idToSearch) || (datas && datas.name ? datas : null)
            return user ? [`- ${user.name} ${user.lastName}`] : ['- Usuario desconocido']
        }
    } else {
        return ['- Sin asignaciones']
    }
}

/**
 * Processes a report's `historialAsignaciones` array to get a unique, formatted list of assigned user names.
 * @param {Array<Object>} historialAsignaciones
 * @returns {Array<string>}
 */
export const historyUsers = (historialAsignaciones) => {
    if (!historialAsignaciones || historialAsignaciones.length === 0) {
        return ['- Sin asignaciones']
    }

    const userNames = historialAsignaciones.map(assignment => {
        const user = assignment.asignadoA
        if (user && user.name && user.lastName) {
            return `- ${user.name} ${user.lastName}`
        }
        return null
    }).filter(Boolean)

    if (userNames.length === 0) {
        return ['- Desconocido']
    }
    return [...new Set(userNames)]
}
